import React, { useEffect, useState } from "react";
import { Partner } from "../../types/partner.type";
import { api } from "../../api/api";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import useGetRequest from "../../hooks/useGetRequest";
import { Machine } from "../../types/machine.type";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Partners() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [form] = Form.useForm();
  const { data: machinesList, loading: machineLoading } =
    useGetRequest("/machine/list");

  useEffect(() => {
    fetchPartners();
  }, [page, pageSize, searchTerm]);

  const fetchPartners = async () => {
    setLoading(true);

    try {
      const response = await api.get("/partners", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
        },
      });

      setPartners(response.data.partners);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching clients", error);
      message.error("Ошибка при получение партнёров.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddPartner = () => {
    form.resetFields();
    setSelectedPartner(null);
    setIsModalOpen(true);
  };

  const handleEditPartner = (partner: Partner) => {
    form.setFieldsValue({
      ...partner,
      actives: partner.actives.map((active) => ({
        machineId: active.machineId,
        percentage: active.percentage,
      })),
    });
    setSelectedPartner(partner);
    setIsModalOpen(true);
  };

  const handleSubmitForm = async (values: Partial<Partner>) => {
    setConfirmLoading(true);

    try {
      if (selectedPartner) {
        await api.put(`/partners/${selectedPartner._id}`, values);
        message.success("Партнёр успешно обновлен.");
      } else {
        await api.post("/partners", values);
        message.success("Партнёр успешно добавлен.");
      }

      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchPartners();
      form.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving partner:", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фамилия",
      dataIndex: "surname",
      key: "surname",
    },
    ...(authState.user?.role === UserRole.Admin
      ? [
        {
          title: "Общий баланс прибыль",
          dataIndex: "totalBalanceProfit",
          key: "totalBalanceProfit",
          render: (_: any, partner: Partner) => roundToThreeDecimals(partner.balance + partner.totalWithdrawn),
        },
        {
          title: "Вывод прибыль",
          dataIndex: "totalWithdrawn",
          key: "totalWithdrawn",
          render: (text: number) => roundToThreeDecimals(text),
        },
        {
          title: "Остаток баланс прибыль",
          dataIndex: "balance",
          key: "balance",
          render: (text: number) => roundToThreeDecimals(text),
        },
        {
          title: "Общий инвестированный",
          dataIndex: "totalInvested",
          key: "totalInvested",
          render: (text: number) => roundToThreeDecimals(text),
        },
        {
          title: "Вывод инвестиции",
          dataIndex: "totalWithdrawnInvestment",
          key: "totalWithdrawnInvestment",
          render: (text: number) => roundToThreeDecimals(text),
        },
        {
          title: "Остаток Баланс инвестиции",
          dataIndex: "balanceInvestments",
          key: "balanceInvestments",
          render: (text: number) => roundToThreeDecimals(text),
        },

        {
          title: "Активы",
          dataIndex: "actives",
          key: "actives",
          render: (
            actives: Array<{
              machineName: string;
              percentage: number;
              amount: number;
            }>
          ) => (
            <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
              {actives.map((active, index) => (
                <li key={index}>
                  {active.machineName}: {active.percentage}% , Сумма:
                  {roundToThreeDecimals(active.amount)}
                </li>
              ))}
            </ul>
          ),
        },
      ]
      : []),
    {
      title: "",
      key: "actions",
      render: (_: any, partner: Partner) => {
        const menu = (
          <Menu>
            {authState.user?.role === UserRole.Admin && (
              <Menu.Item onClick={() => handleEditPartner(partner)}>
                Изменить
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() =>
                navigate(`/${partner._id}/${partner.name}/investments`)
              }
            >
              Смотреть инвестиции
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${partner._id}/${partner.name}/withdrawals`)
              }
            >
              Смотреть выводы
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/partner-logs/${partner._id}/${partner.name}`)
              }
            >
              Смотреть логи
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Партнёры </Title>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        {authState.user?.role === UserRole.Admin && (
          <Button type="primary" onClick={handleAddPartner}>
            Добавить
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={partners}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={selectedPartner ? "Изменить партнёра" : "Добавить партнёра"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="surname"
            label="Фамилия"
            rules={[{ required: true, message: "Введите фамилию" }]}
          >
            <Input />
          </Form.Item>

          <Form.List
            name="actives"
            initialValue={selectedPartner ? selectedPartner.actives : []}
            rules={[
              {
                validator: async (_, actives) => {
                  if (!actives || actives.length < 1) {
                    return Promise.reject(
                      new Error(
                        "Необходимо добавить хотя бы одно активное оборудование"
                      )
                    );
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "machineId"]}
                      fieldKey={[fieldKey ?? 0, "machineId"]}
                      label="Машина"
                      rules={[{ required: true, message: "Выберите машину" }]}
                    >
                      <Select
                        loading={machineLoading}
                        placeholder="Выберите машину"
                      >
                        {machinesList?.map((machine: Machine) => (
                          <Option key={machine._id} value={machine._id}>
                            {machine.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "percentage"]}
                      fieldKey={[fieldKey ?? 0, "percentage"]}
                      label="Процент"
                      rules={[{ required: true, message: "Введите процент" }]}
                    >
                      <InputNumber min={0} max={100} />
                    </Form.Item>

                    <Button type="link" onClick={() => remove(name)}>
                      Удалить
                    </Button>
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<DownOutlined />}
                  >
                    Добавить актив
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
